import { useEffect, useState } from 'react';
import { listObservations, type ObservationSummary } from './api/endpoints.js';
import { session } from './api/session.js';

/**
 * What a contributor's listening adds up to — derived entirely on the
 * client, from `listObservations`.
 *
 * This is the "interim, client-side" option the Field record handoff names
 * explicitly, not the "proper" one: there is no `GET /v1/me/standing` yet, so
 * `clean audio` and `lossless` counts (not present on `ObservationSummary`)
 * and corpus-wide rarity/peer figures (need data no single client has) simply
 * cannot be computed here. Per that handoff: hide what can't be shown
 * honestly, rather than fake it. Whoever builds the real endpoint should be
 * able to delete this file and swap in a single `getStanding()` call —
 * `computeStanding`'s shape is deliberately close to the `Standing` interface
 * that handoff sketches, for exactly that reason.
 */

export interface ObservationDigest {
  /** Everything that still counts: submitted, and not rejected in review. */
  total: number;
  bySubject: Record<string, number>;
  /** Newest `createdAt` seen for each subject. */
  lastObservedAtBySubject: Record<string, string>;
  /** Observations with antecedent, signal and consequence all coded. */
  fullContext: number;
  /** Of `total`, how many no reviewer has looked at yet. Counted, but not final. */
  pending: number;
  /**
   * Rejected in review, and therefore excluded from every count above —
   * including the ones credits and stamps are derived from.
   */
  rejected: number;
  /** True if there were more observations than this digest looked at. */
  truncated: boolean;
}

/**
 * Caps how much history a page load will fetch. A contributor with a
 * genuinely enormous corpus gets an approximate (but always honestly
 * under-, never over-, counted) standing rather than a slow page — `total`
 * and the stamp thresholds below all top out well under this.
 */
const MAX_ITEMS = 500;
const PAGE_SIZE = 100;

export async function fetchObservationDigest(): Promise<ObservationDigest> {
  const bySubject: Record<string, number> = {};
  const lastObservedAtBySubject: Record<string, string> = {};
  let total = 0;
  let fullContext = 0;
  let pending = 0;
  let rejected = 0;
  let truncated = false;
  let cursor: string | undefined;

  // Counts rows looked at rather than rows kept, so the cap below still bounds
  // the work done when many of them are rejected.
  let scanned = 0;

  for (;;) {
    const page = await listObservations({ limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) });

    for (const observation of page.items) {
      scanned++;

      // A rejected observation counts for nothing: not the total, not the
      // stamps, not the credits derived from both. That is the whole point of
      // a verification status — until rejection costs something, "accepted"
      // means "uploaded", and a corpus that pays for uploads gets uploads.
      if (observation.verificationStatus === 'rejected') {
        rejected++;
        continue;
      }
      if (observation.verificationStatus === 'pending') pending++;

      total++;
      const subjectId = subjectIdOf(observation);
      if (subjectId) {
        bySubject[subjectId] = (bySubject[subjectId] ?? 0) + 1;
        // Newest-first pagination means the first time a subject is seen here
        // is its most recent observation.
        if (!lastObservedAtBySubject[subjectId]) {
          lastObservedAtBySubject[subjectId] = observation.createdAt;
        }
      }
      if (isFullContext(observation)) fullContext++;
    }

    if (!page.nextCursor || page.items.length === 0) break;
    if (scanned >= MAX_ITEMS) {
      truncated = true;
      break;
    }
    cursor = page.nextCursor;
  }

  return { total, bySubject, lastObservedAtBySubject, fullContext, pending, rejected, truncated };
}

// ── Shared cache ─────────────────────────────────────────────────────────
//
// Contribute, Timeline and Field record all want this digest on mount.
// Without a shared cache, moving between them re-runs the same up-to-5-request
// pagination three times. One in-flight fetch and one cache, the same shape
// as the refresh lock in api/session.ts.

let cachedDigest: ObservationDigest | null = null;
let inFlight: Promise<ObservationDigest> | null = null;
const listeners = new Set<(digest: ObservationDigest) => void>();

/** The cached digest if one exists, otherwise the single shared in-flight fetch. */
export function getObservationDigest(): Promise<ObservationDigest> {
  if (cachedDigest) return Promise.resolve(cachedDigest);
  if (inFlight) return inFlight;

  inFlight = fetchObservationDigest()
    .then((digest) => {
      cachedDigest = digest;
      for (const listener of listeners) listener(digest);
      return digest;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Call after submitting a new observation — the cache is now one stale. */
export function invalidateObservationDigest(): void {
  cachedDigest = null;
}

// A cache keyed by nothing but "the current session" must not survive a
// sign-out — otherwise a second contributor on the same browser tab would
// see the first one's counts until something happened to trigger a refetch.
session.onChange(() => invalidateObservationDigest());

/**
 * The digest, kept in sync with whichever caller last refreshed it.
 * Contribute, Timeline and Field record all use this instead of calling
 * `fetchObservationDigest` directly, so they share one cache and one
 * in-flight request between them.
 */
export function useObservationDigest(): {
  digest: ObservationDigest | null;
  error: Error | null;
  refresh: () => void;
} {
  const [digest, setDigest] = useState<ObservationDigest | null>(cachedDigest);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    listeners.add(setDigest);
    getObservationDigest()
      .then((d) => {
        if (!cancelled) setDigest(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
      listeners.delete(setDigest);
    };
  }, []);

  const refresh = () => {
    invalidateObservationDigest();
    getObservationDigest()
      .then(setDigest)
      .catch((e: Error) => setError(e));
  };

  return { digest, error, refresh };
}

type Rec = Record<string, unknown>;

function subjectIdOf(observation: ObservationSummary): string | null {
  const subject = (observation.record as Rec).subject as Rec | undefined;
  return typeof subject?.id === 'string' ? subject.id : null;
}

function isFullContext(observation: ObservationSummary): boolean {
  const record = observation.record as Rec;

  const segments = record.segments;
  const hasSignal =
    Array.isArray(segments) &&
    segments.some((segment) => {
      const annotations = (segment as Rec).annotations;
      return (
        Array.isArray(annotations) &&
        annotations.some((a) => {
          const signalType = (a as Rec).signal_type;
          return typeof signalType === 'string' && signalType !== '';
        })
      );
    });

  const antecedent = record.antecedent as Rec | undefined;
  const hasAntecedent = Boolean(
    antecedent &&
      ((Array.isArray(antecedent.coded_terms) && antecedent.coded_terms.length > 0) ||
        (typeof antecedent.description_free === 'string' && antecedent.description_free.trim())),
  );

  const consequence = record.consequence as Rec | undefined;
  const hasConsequence = Boolean(
    consequence &&
      typeof consequence.animal_next_action === 'string' &&
      consequence.animal_next_action.trim(),
  );

  return hasSignal && hasAntecedent && hasConsequence;
}

// ── Volume stamps ────────────────────────────────────────────────────────

export const VOLUME_THRESHOLDS = [1, 10, 25, 100] as const;

const VOLUME_STAMP_LABEL: Record<(typeof VOLUME_THRESHOLDS)[number], string> = {
  1: 'First take',
  10: 'Ten in',
  25: 'Twenty-five',
  100: 'The hundred',
};

/**
 * Where a contributor sits on the volume ladder: the threshold they're
 * building toward, the one they last cleared, and a fill fraction with the
 * doc's explicit 4% floor so zero progress still reads as a bar.
 */
export function nextVolumeMilestone(total: number): {
  prev: number;
  next: number | null;
  fraction: number;
} {
  const next = VOLUME_THRESHOLDS.find((t) => t > total) ?? null;
  const prev = [...VOLUME_THRESHOLDS].reverse().find((t) => t <= total) ?? 0;
  if (next === null) return { prev, next, fraction: 1 };
  const fraction = Math.max(0.04, (total - prev) / (next - prev));
  return { prev, next, fraction };
}

export interface Stamp {
  key: string;
  label: string;
  note: string;
  on: boolean;
}

/**
 * Only the stamps this digest can honestly evaluate: the four volume
 * thresholds and "Full context". "Clean take", "Lossless" and "Rare find"
 * are omitted outright, not shown-and-unearned — `ObservationSummary` has no
 * clipping or lossless flag, and rarity needs corpus-wide counts no client
 * has. Hiding them is what the handoff asks for; showing them as perpetually
 * unearned would be showing something false.
 */
export function computeStamps(digest: ObservationDigest): Stamp[] {
  const volume: Stamp[] = VOLUME_THRESHOLDS.map((t) => ({
    key: `volume-${t}`,
    label: VOLUME_STAMP_LABEL[t],
    note: t === 1 ? 'One recording' : t === 100 ? '100 recordings' : `${t} recordings`,
    on: digest.total >= t,
  }));

  return [
    ...volume,
    {
      key: 'full-context',
      label: 'Full context',
      note: '10 with A/B/C filled',
      on: digest.fullContext >= 10,
    },
  ];
}

export interface CreditRule {
  key: string;
  label: string;
  rate: number;
  count: number;
}

/**
 * Only the two credit rules this digest can compute: +10 per accepted
 * recording, +5 per fully-coded A/B/C. "Clean audio" (+5) and "rare call"
 * (+15) are omitted for the same reason the stamps above are — no clipping
 * flag, no corpus-wide rarity data client-side. The displayed total is the
 * sum of exactly the rows shown, so it stays checkable by eye even though
 * it's a floor on the real (server-computed) balance, not the real balance.
 */
export function computeCreditRules(digest: ObservationDigest): CreditRule[] {
  return [
    { key: 'accepted', label: 'Each recording accepted', rate: 10, count: digest.total },
    { key: 'full-context', label: 'Full A / B / C context', rate: 5, count: digest.fullContext },
  ];
}

export function totalCredits(rules: CreditRule[]): number {
  return rules.reduce((sum, r) => sum + r.rate * r.count, 0);
}

// ── Formatting ───────────────────────────────────────────────────────────

/** 1 → "1st", 4 → "4th", 11 → "11th", 22 → "22nd". */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** "2d ago" / "Not yet" — day-level, which is all this app's cadence needs. */
export function relativeTime(iso: string | undefined): string {
  if (!iso) return 'Not yet';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
